// Internationalization utility for BetVerse
// Supports: English, Spanish, Portuguese, French

export type SupportedLanguage = 'en' | 'es' | 'pt' | 'fr';

export const translations = {
  en: {
    // Navigation
    nav: {
      home: 'Home',
      tipsters: 'Tipsters',
      tips: 'Tips',
      leaderboard: 'Leaderboard',
      signIn: 'Sign In',
      signOut: 'Sign Out',
      profile: 'Profile',
      interactiveTips: 'Interactive Tips',
      live: 'Live',
      dashboard: 'Dashboard',
      myDashboard: 'My Dashboard',
      becomeTipster: 'Become Tipster',
      settings: 'Settings',
      getStarted: 'Get Started'
    },
    
    // Hero Section
    hero: {
      title: "Connect with the World's Best Tipsters",
      subtitle: 'Access verified betting insights from professional tipsters worldwide. AI-powered predictions, real-time performance tracking, and secure payments in your local currency.',
      exploreTipsters: 'Explore Tipsters',
      viewTips: 'View Tips',
      joinAsBettor: 'Join as Bettor',
      becomeTipster: 'Become a Tipster'
    },
    
    // Common Terms
    common: {
      free: 'FREE',
      premium: 'Premium',
      ai: 'AI',
      vip: 'VIP',
      verified: 'Verified',
      loading: 'Loading...',
      error: 'Error',
      success: 'Success',
      cancel: 'Cancel',
      confirm: 'Confirm',
      save: 'Save',
      edit: 'Edit',
      delete: 'Delete',
      search: 'Search',
      filter: 'Filter',
      viewAll: 'View All',
      loadMore: 'Load More',
      share: 'Share',
      credits: 'credits'
    },
    
    // Tips & Betting
    betting: {
      roi: 'ROI',
      winRate: 'Win Rate',
      confidence: 'Confidence',
      odds: 'Odds',
      prediction: 'Prediction',
      expiresIn: 'Expires in',
      expired: 'Expired',
      viewTip: 'View Tip',
      purchaseTip: 'Purchase Tip',
      freeTip: 'Get Free Tip',
      tipExpired: 'This tip has expired',
      tipUnavailable: 'This tip is no longer available'
    },
    
    // Tipster Profile
    tipster: {
      totalTips: 'Total Tips',
      streak: 'Streak',
      followers: 'followers',
      follow: 'Follow',
      subscribe: 'Subscribe',
      joined: 'Joined',
      recentTips: 'Recent Tips',
      performance: 'Performance',
      overview: 'Overview'
    },
    
    // Age Verification
    ageVerification: {
      title: 'Age Verification Required',
      subtitle: 'You must be 18+ to access betting tips and predictions',
      important: 'Important: BetVerse is a tip marketplace, not a sportsbook. We provide betting insights and predictions only.',
      ageRequirements: 'Age Requirements',
      responsibleGambling: 'Responsible Gambling',
      legalDisclaimer: 'Legal Disclaimer: BetVerse provides sports betting tips and predictions for entertainment and educational purposes. We are not a licensed sportsbook or gambling operator. Users are responsible for complying with local gambling laws and regulations.',
      confirmAge: 'I confirm that I am 18+ years old and legally permitted to access betting-related content in my jurisdiction',
      acknowledgeRisks: 'I understand the risks of gambling and agree to gamble responsibly',
      underAge: "I'm Under 18",
      verifyEnter: 'Verify & Enter'
    },
    
    // Currency Formatting
    currency: {
      symbol: '$',
      code: 'USD'
    }
  },
  
  es: {
    nav: {
      home: 'Inicio',
      tipsters: 'Pronosticadores',
      tips: 'Pronósticos',
      leaderboard: 'Clasificación',
      signIn: 'Iniciar Sesión',
      signOut: 'Cerrar Sesión',
      profile: 'Perfil',
      interactiveTips: 'Tips Interactivos',
      live: 'En Vivo',
      dashboard: 'Panel',
      myDashboard: 'Mi Panel',
      becomeTipster: 'Ser Pronosticador',
      settings: 'Configuración',
      getStarted: 'Comenzar'
    },
    
    hero: {
      title: 'Conéctate con los Mejores Pronosticadores del Mundo',
      subtitle: 'Accede a pronósticos verificados de pronosticadores profesionales de todo el mundo. Predicciones impulsadas por IA, seguimiento de rendimiento en tiempo real y pagos seguros en tu moneda local.',
      exploreTipsters: 'Explorar Pronosticadores',
      viewTips: 'Ver Pronósticos',
      joinAsBettor: 'Únete como Apostador',
      becomeTipster: 'Conviértete en Pronosticador'
    },
    
    common: {
      free: 'GRATIS',
      premium: 'Premium',
      ai: 'IA',
      vip: 'VIP',
      verified: 'Verificado',
      loading: 'Cargando...',
      error: 'Error',
      success: 'Éxito',
      cancel: 'Cancelar',
      confirm: 'Confirmar',
      save: 'Guardar',
      edit: 'Editar',
      delete: 'Eliminar',
      search: 'Buscar',
      filter: 'Filtrar',
      viewAll: 'Ver Todo',
      loadMore: 'Cargar Más',
      share: 'Compartir',
      credits: 'créditos'
    },
    
    betting: {
      roi: 'ROI',
      winRate: 'Tasa de Acierto',
      confidence: 'Confianza',
      odds: 'Cuotas',
      prediction: 'Predicción',
      expiresIn: 'Expira en',
      expired: 'Expirado',
      viewTip: 'Ver Pronóstico',
      purchaseTip: 'Comprar Pronóstico',
      freeTip: 'Obtener Pronóstico Gratis',
      tipExpired: 'Este pronóstico ha expirado',
      tipUnavailable: 'Este pronóstico ya no está disponible'
    },
    
    tipster: {
      totalTips: 'Pronósticos Totales',
      streak: 'Racha',
      followers: 'seguidores',
      follow: 'Seguir',
      subscribe: 'Suscribirse',
      joined: 'Se unió',
      recentTips: 'Pronósticos Recientes',
      performance: 'Rendimiento',
      overview: 'Resumen'
    },
    
    ageVerification: {
      title: 'Verificación de Edad Requerida',
      subtitle: 'Debes tener 18+ años para acceder a pronósticos de apuestas',
      important: 'Importante: BetVerse es un mercado de pronósticos, no una casa de apuestas. Solo proporcionamos análisis y predicciones.',
      ageRequirements: 'Requisitos de Edad',
      responsibleGambling: 'Juego Responsable',
      legalDisclaimer: 'Descargo Legal: BetVerse proporciona pronósticos de apuestas deportivas con fines de entretenimiento y educación. No somos una casa de apuestas con licencia. Los usuarios son responsables de cumplir con las leyes locales.',
      confirmAge: 'Confirmo que tengo 18+ años y estoy legalmente autorizado para acceder a contenido relacionado con apuestas en mi jurisdicción',
      acknowledgeRisks: 'Entiendo los riesgos del juego y acepto apostar de manera responsable',
      underAge: 'Soy Menor de 18',
      verifyEnter: 'Verificar y Entrar'
    },
    
    currency: {
      symbol: '€',
      code: 'EUR'
    }
  },
  
  pt: {
    nav: {
      home: 'Início',
      tipsters: 'Tipsters',
      tips: 'Dicas',
      leaderboard: 'Classificação',
      signIn: 'Entrar',
      signOut: 'Sair',
      profile: 'Perfil',
      interactiveTips: 'Dicas Interativas',
      live: 'Ao Vivo',
      dashboard: 'Painel',
      myDashboard: 'Meu Painel',
      becomeTipster: 'Seja Tipster',
      settings: 'Configurações',
      getStarted: 'Começar'
    },
    
    hero: {
      title: 'Conecte-se com os Melhores Tipsters do Mundo',
      subtitle: 'Acesse análises verificadas de tipsters profissionais em todo o mundo. Previsões com IA, acompanhamento de desempenho em tempo real e pagamentos seguros na sua moeda local.',
      exploreTipsters: 'Explorar Tipsters',
      viewTips: 'Ver Dicas',
      joinAsBettor: 'Junte-se como Apostador',
      becomeTipster: 'Torne-se um Tipster'
    },
    
    common: {
      free: 'GRÁTIS',
      premium: 'Premium',
      ai: 'IA',
      vip: 'VIP',
      verified: 'Verificado',
      loading: 'Carregando...',
      error: 'Erro',
      success: 'Sucesso',
      cancel: 'Cancelar',
      confirm: 'Confirmar',
      save: 'Salvar',
      edit: 'Editar',
      delete: 'Excluir',
      search: 'Pesquisar',
      filter: 'Filtrar',
      viewAll: 'Ver Tudo',
      loadMore: 'Carregar Mais',
      share: 'Compartilhar',
      credits: 'créditos'
    },
    
    betting: {
      roi: 'ROI',
      winRate: 'Taxa de Acerto',
      confidence: 'Confiança',
      odds: 'Odds',
      prediction: 'Previsão',
      expiresIn: 'Expira em',
      expired: 'Expirado',
      viewTip: 'Ver Dica',
      purchaseTip: 'Comprar Dica',
      freeTip: 'Obter Dica Grátis',
      tipExpired: 'Esta dica expirou',
      tipUnavailable: 'Esta dica não está mais disponível'
    },
    
    tipster: {
      totalTips: 'Total de Dicas',
      streak: 'Sequência',
      followers: 'seguidores',
      follow: 'Seguir',
      subscribe: 'Assinar',
      joined: 'Entrou',
      recentTips: 'Dicas Recentes',
      performance: 'Desempenho',
      overview: 'Visão Geral'
    },
    
    ageVerification: {
      title: 'Verificação de Idade Necessária',
      subtitle: 'Você deve ter 18+ anos para acessar dicas de apostas',
      important: 'Importante: BetVerse é um marketplace de dicas, não uma casa de apostas. Fornecemos apenas análises e previsões.',
      ageRequirements: 'Requisitos de Idade',
      responsibleGambling: 'Jogo Responsável',
      legalDisclaimer: 'Aviso Legal: BetVerse fornece dicas de apostas esportivas para fins de entretenimento e educação. Não somos uma casa de apostas licenciada. Os usuários são responsáveis por cumprir as leis locais.',
      confirmAge: 'Confirmo que tenho 18+ anos e estou legalmente autorizado a acessar conteúdo relacionado a apostas na minha jurisdição',
      acknowledgeRisks: 'Entendo os riscos do jogo e concordo em apostar com responsabilidade',
      underAge: 'Sou Menor de 18',
      verifyEnter: 'Verificar e Entrar'
    },
    
    currency: {
      symbol: 'R$',
      code: 'BRL'
    }
  },
  
  fr: {
    nav: {
      home: 'Accueil',
      tipsters: 'Pronostiqueurs',
      tips: 'Pronostics',
      leaderboard: 'Classement',
      signIn: 'Se Connecter',
      signOut: 'Se Déconnecter',
      profile: 'Profil',
      interactiveTips: 'Tips Interactifs',
      live: 'En Direct',
      dashboard: 'Tableau de Bord',
      myDashboard: 'Mon Tableau',
      becomeTipster: 'Devenir Pronostiqueur',
      settings: 'Paramètres',
      getStarted: 'Commencer'
    },
    
    hero: {
      title: 'Connectez-vous avec les Meilleurs Pronostiqueurs du Monde',
      subtitle: 'Accédez aux analyses vérifiées de pronostiqueurs professionnels du monde entier. Prédictions alimentées par IA, suivi des performances en temps réel et paiements sécurisés dans votre devise locale.',
      exploreTipsters: 'Explorer les Pronostiqueurs',
      viewTips: 'Voir les Pronostics',
      joinAsBettor: 'Rejoindre en tant que Parieur',
      becomeTipster: 'Devenir Pronostiqueur'
    },
    
    common: {
      free: 'GRATUIT',
      premium: 'Premium',
      ai: 'IA',
      vip: 'VIP',
      verified: 'Vérifié',
      loading: 'Chargement...',
      error: 'Erreur',
      success: 'Succès',
      cancel: 'Annuler',
      confirm: 'Confirmer',
      save: 'Sauvegarder',
      edit: 'Modifier',
      delete: 'Supprimer',
      search: 'Rechercher',
      filter: 'Filtrer',
      viewAll: 'Voir Tout',
      loadMore: 'Charger Plus',
      share: 'Partager',
      credits: 'crédits'
    },
    
    betting: {
      roi: 'ROI',
      winRate: 'Taux de Réussite',
      confidence: 'Confiance',
      odds: 'Cotes',
      prediction: 'Prédiction',
      expiresIn: 'Expire dans',
      expired: 'Expiré',
      viewTip: 'Voir le Pronostic',
      purchaseTip: 'Acheter le Pronostic',
      freeTip: 'Obtenir le Pronostic Gratuit',
      tipExpired: 'Ce pronostic a expiré',
      tipUnavailable: 'Ce pronostic n\'est plus disponible'
    },
    
    tipster: {
      totalTips: 'Pronostics Totaux',
      streak: 'Série',
      followers: 'abonnés',
      follow: 'Suivre',
      subscribe: 'S\'abonner',
      joined: 'Rejoint',
      recentTips: 'Pronostics Récents',
      performance: 'Performance',
      overview: 'Aperçu'
    },
    
    ageVerification: {
      title: 'Vérification d\'Âge Requise',
      subtitle: 'Vous devez avoir 18+ ans pour accéder aux pronostics de paris',
      important: 'Important: BetVerse est une place de marché de pronostics, pas un bookmaker. Nous fournissons uniquement des analyses et prédictions.',
      ageRequirements: 'Exigences d\'Âge',
      responsibleGambling: 'Jeu Responsable',
      legalDisclaimer: 'Avertissement Légal: BetVerse fournit des pronostics de paris sportifs à des fins de divertissement et d\'éducation. Nous ne sommes pas un bookmaker licencié. Les utilisateurs sont responsables du respect des lois locales.',
      confirmAge: 'Je confirme que j\'ai 18+ ans et que je suis légalement autorisé à accéder au contenu lié aux paris dans ma juridiction',
      acknowledgeRisks: 'Je comprends les risques du jeu et accepte de jouer de manière responsable',
      underAge: 'J\'ai Moins de 18 Ans',
      verifyEnter: 'Vérifier et Entrer'
    },
    
    currency: {
      symbol: '€',
      code: 'EUR'
    }
  }
};

// Get browser language or default to English
export const getBrowserLanguage = (): SupportedLanguage => {
  const lang = navigator.language.split('-')[0];
  return Object.keys(translations).includes(lang) ? lang as SupportedLanguage : 'en';
};

// Translation hook
export const useTranslation = (language: SupportedLanguage = getBrowserLanguage()) => {
  const t = (key: string): string => {
    const keys = key.split('.');
    let value: any = translations[language];
    
    for (const k of keys) {
      value = value?.[k];
    }
    
    return value || key;
  };

  const formatCurrency = (amount: number): string => {
    const currencyData = translations[language].currency;
    return `${currencyData.symbol}${amount.toFixed(2)}`;
  };

  return { t, formatCurrency, language };
};